import type { PersonaEvent } from '@strategy4kids/persona';
import { useEffect, useRef, useState } from 'react';
import { usePersonaStore } from '../state/persona-store.js';
import { BUBBLE_MS, chatterSuppressed } from '../state/table-chatter.js';

export interface BackgammonBubbleInput {
  /** Off while online (the opponent is a person) or before the game is set up. */
  readonly enabled: boolean;
  readonly phase: string;
  readonly barMine: number;
  readonly barTheirs: number;
  readonly offTheirs: number;
  /** The winner's side once the game is over. */
  readonly winner: string | null;
  readonly mySide: string;
  readonly locale: 'en' | 'fr' | 'es';
}

/**
 * The robot's line for the opponent's player bar, from EXISTING persona events only (no new
 * backgammon events): game-start, `encourage` when it hits one of your checkers, `lose` when you
 * hit one of its checkers, `win` when it bears off its first checker, and win/lose at the end.
 * Everything comes from `persona-store.lineFor`, so the chatter level, the parent locks and the
 * per-game limiter all apply; public facts only (the board is open). The line fades after a few seconds.
 */
export function useBackgammonBubble(input: BackgammonBubbleInput): string | null {
  const { enabled, phase, barMine, barTheirs, offTheirs, winner, mySide, locale } = input;
  const [line, setLine] = useState<string | null>(null);
  const prev = useRef({ barMine, barTheirs, offTheirs, phase });
  const started = useRef(false);

  const say = (event: PersonaEvent) => {
    if (chatterSuppressed()) return;
    const next = usePersonaStore.getState().lineFor(event, locale);
    if (next) setLine(next);
  };

  useEffect(() => {
    if (!enabled || started.current) return;
    started.current = true;
    usePersonaStore.getState().resetSpoken();
    say('game-start');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const p = prev.current;
    if (phase === 'gameover' && p.phase !== 'gameover') say(winner === null ? 'encourage' : winner === mySide ? 'lose' : 'win');
    else if (phase === 'playing') {
      if (barMine > p.barMine) say('encourage');
      else if (barTheirs > p.barTheirs) say('lose');
      else if (offTheirs > 0 && p.offTheirs === 0) say('win');
    }
    prev.current = { barMine, barTheirs, offTheirs, phase };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, phase, barMine, barTheirs, offTheirs, winner]);

  useEffect(() => {
    if (!line) return;
    const t = setTimeout(() => setLine(null), BUBBLE_MS);
    return () => clearTimeout(t);
  }, [line]);

  return enabled ? line : null;
}
